import { endOfDay, format, startOfDay, startOfToday } from 'date-fns'
import React from 'react'
import { Helmet } from 'react-helmet'
import { Message } from 'semantic-ui-react'

import PageHeader from '../../components/pageHeader/pageHeader'
import DateSelect from '../../components/form/dateSelect'
import UserSelect from '../../components/form/userSelect'
import PageLoader from '../../components/pageLoader'
import DataTable from '../../components/dataTable/dataTable'
import CheckButton from '../../components/form/checkButton'
import EntityLink from '../../components/remoteValues/entityLink'
import { BillingPeriod, Task, User } from '../../types'
import useDoneTasks from '../../hooks/useDoneTasks'
import DateFormat from '../../components/dateFormat'
import UserName from '../../components/remoteValues/userName'

type GroupSource = 'doneBy' | 'assignedTo' | 'unknown'

interface TaskGroup {
  userId?: User['_id']
  tasks: Task[]
  source: GroupSource
  key: string
}

const DoneTasksPage = () => {
  const [selectedDate, setSelectedDate] = React.useState<Date>(() =>
    startOfToday(),
  )
  const [selectedUserId, setSelectedUserId] = React.useState<
    User['_id'] | undefined
  >()

  const periodStart = React.useMemo(
    () => startOfDay(selectedDate).getTime(),
    [selectedDate],
  )
  const periodEnd = React.useMemo(
    () => endOfDay(selectedDate).getTime(),
    [selectedDate],
  )

  const doneTasks = useDoneTasks({
    from: periodStart,
    to: periodEnd,
    userId: selectedUserId,
  })

  const groups = React.useMemo<TaskGroup[]>(() => {
    const map = new Map<string, TaskGroup>()
    const ordered: TaskGroup[] = []

    ;(doneTasks.data || []).forEach((task) => {
      const userId = task.doneBy || task.assignedToId
      const source: GroupSource = task.doneBy
        ? 'doneBy'
        : task.assignedToId
          ? 'assignedTo'
          : 'unknown'
      const key = userId || '__unknown__'

      const current =
        map.get(key) ||
        (() => {
          const group: TaskGroup = { userId, source, tasks: [], key }
          map.set(key, group)
          ordered.push(group)
          return group
        })()

      if (current.source !== 'doneBy' && source === 'doneBy') {
        current.source = 'doneBy'
      }

      current.tasks.push(task)
    })

    return ordered.map((group) => ({
      ...group,
      tasks: [...group.tasks].sort((a, b) => (b.doneOn || 0) - (a.doneOn || 0)),
    }))
  }, [doneTasks.data])

  const totalTasks = doneTasks.data?.length ?? 0

  return (
    <>
      <Helmet>
        <title>Done Tasks</title>
      </Helmet>

      <PageHeader
        title="Completed Tasks"
        sub={`Overview of tasks marked as done on ${format(
          selectedDate,
          'PPP',
        )}.`}
        actions={
          <div className="flex flex-wrap gap-3">
            <div className="min-w-[220px]">
              <label className="mb-1 block text-xs font-semibold uppercase text-slate-500">
                Day
              </label>
              <DateSelect
                value={selectedDate}
                onChange={(
                  _event: React.SyntheticEvent<HTMLElement>,
                  payload: { value?: number | null },
                ) => {
                  if (!payload?.value) {
                    setSelectedDate(startOfToday())
                    return
                  }
                  setSelectedDate(startOfDay(new Date(payload.value)))
                }}
              />
            </div>
            <div className="min-w-[240px]">
              <label className="mb-1 block text-xs font-semibold uppercase text-slate-500">
                User
              </label>
              <UserSelect
                placeholder="All users"
                search
                value={selectedUserId}
                onChange={(value) => {
                  const nextValue = value && (value as unknown as User['_id'])
                  setSelectedUserId(nextValue || undefined)
                }}
              />
            </div>
          </div>
        }
      />

      {doneTasks.isLoading && <PageLoader />}

      {!doneTasks.isLoading && doneTasks.error && (
        <div className="mx-14">
          <Message
            negative
            header="We couldn't load the completed tasks"
            content={doneTasks.error.message}
          />
        </div>
      )}

      {!doneTasks.isLoading && !doneTasks.error && (
        <div className="mx-14 my-6 space-y-10">
          <div className="text-sm text-slate-500">
            {totalTasks === 0
              ? 'There are no completed tasks in the selected period.'
              : `${totalTasks} ${
                  totalTasks === 1 ? 'task completed' : 'tasks completed'
                } in total.`}
          </div>

          {groups.map((group) => (
            <section key={group.key} className="space-y-3">
              <header className="flex flex-wrap items-baseline justify-between gap-2">
                <div className="text-lg font-semibold text-slate-700">
                  {group.userId ? (
                    <UserName id={group.userId} />
                  ) : (
                    'Unassigned user'
                  )}
                </div>
                <div className="text-sm text-slate-500">
                  {group.tasks.length}{' '}
                  {group.tasks.length === 1
                    ? 'task completed'
                    : 'tasks completed'}
                  {group.source === 'assignedTo' && (
                    <span className="ml-2 text-xs uppercase text-slate-400">
                      (missing doneBy)
                    </span>
                  )}
                  {group.source === 'unknown' && (
                    <span className="ml-2 text-xs uppercase text-slate-400">
                      (missing assignment)
                    </span>
                  )}
                </div>
              </header>

              <DataTable>
                <thead>
                  <tr>
                    <DataTable.Th width="1%" align="center">
                      Done
                    </DataTable.Th>
                    <DataTable.Th width={220}>Subject</DataTable.Th>
                    <DataTable.Th>Details</DataTable.Th>
                    <DataTable.Th width="1%" className="whitespace-nowrap">
                      Related To
                    </DataTable.Th>
                    <DataTable.Th width="1%" className="whitespace-nowrap">
                      Done Date
                    </DataTable.Th>
                    <DataTable.Th width="1%" className="whitespace-nowrap">
                      Assigned To
                    </DataTable.Th>
                    <DataTable.Th width="1%" className="whitespace-nowrap">
                      Completed By
                    </DataTable.Th>
                  </tr>
                </thead>
                <tbody>
                  {group.tasks.map((task) => (
                    <tr key={task._id}>
                      <DataTable.Td align="center" valign="middle">
                        <CheckButton value disabled onChange={() => {}} />
                      </DataTable.Td>
                      <DataTable.Td className="align-baseline !text-base font-semibold text-slate-800">
                        {task.subject}
                      </DataTable.Td>
                      <DataTable.Td className="align-baseline">
                        {task.details ? (
                          <div
                            className="text-sm leading-relaxed text-slate-600"
                            dangerouslySetInnerHTML={{ __html: task.details }}
                          />
                        ) : (
                          <span className="text-sm text-slate-400">
                            No details provided
                          </span>
                        )}

                        {task.doneNote && (
                          <div className="mt-3 rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
                            <div className="text-xs font-semibold uppercase text-slate-400">
                              Completion note
                            </div>
                            <div
                              className="mt-1"
                              dangerouslySetInnerHTML={{
                                __html: task.doneNote,
                              }}
                            />
                          </div>
                        )}
                      </DataTable.Td>
                      <DataTable.Td className="whitespace-nowrap align-baseline">
                        {task.relatedCollection &&
                        task.relatedCollection !== 'nothing' ? (
                          <EntityLink
                            entityId={task.relatedId}
                            collection={task.relatedCollection}
                            linkTo={
                              task.relatedCollection === 'billing-periods'
                                ? (period: BillingPeriod) =>
                                    `/billing/${period._id}`
                                : undefined
                            }
                            render={
                              task.relatedCollection === 'payrolls'
                                ? (payroll) => `Payroll: ${payroll.name}`
                                : task.relatedCollection === 'billing-periods'
                                  ? (period: BillingPeriod) => (
                                      <div>
                                        Billing period:
                                        <div className="-mt-0.5 text-base">
                                          {format(
                                            period.periodStart,
                                            'yyyy-MM-dd',
                                          )}{' '}
                                          &ndash;{' '}
                                          {format(
                                            period.periodEnd,
                                            'yyyy-MM-dd',
                                          )}
                                        </div>
                                      </div>
                                    )
                                  : undefined
                            }
                          />
                        ) : (
                          'Nothing related'
                        )}
                      </DataTable.Td>
                      <DataTable.Td className="whitespace-nowrap align-baseline">
                        {task.doneOn ? (
                          <DateFormat date={task.doneOn} hours />
                        ) : (
                          '—'
                        )}
                      </DataTable.Td>
                      <DataTable.Td className="whitespace-nowrap align-baseline">
                        {task.assignedToId ? (
                          <UserName id={task.assignedToId} short />
                        ) : (
                          '—'
                        )}
                      </DataTable.Td>
                      <DataTable.Td className="whitespace-nowrap align-baseline">
                        {task.doneBy ? (
                          <UserName id={task.doneBy} short />
                        ) : (
                          '—'
                        )}
                      </DataTable.Td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            </section>
          ))}

          {groups.length === 0 && totalTasks === 0 && (
            <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-6 py-16 text-center text-sm text-slate-500">
              We couldn&apos;t find completed tasks for the selected filters.
            </div>
          )}
        </div>
      )}
    </>
  )
}

export default DoneTasksPage
